import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";

export type TenantStatus = "active" | "suspended" | "archived";

export interface TenantRecord {
    id: string;
    slug: string;
    name: string;
    status: TenantStatus;
    userCount: number;
    activeLicenseCount: number;
    country?: string;
    createdAt: string;
    updatedAt: string;
}

export type TenantContactKind =
    | "primary"
    | "billing"
    | "technical"
    | "legal"
    | "security";

export interface TenantContactRecord {
    id: string;
    tenantId: string;
    kind: TenantContactKind;
    name: string;
    email: string;
    phone?: string;
    title?: string;
    notes?: string;
    createdAt: string;
    updatedAt: string;
}

export interface TenantDetailRecord extends TenantRecord {
    legalName?: string;
    industry?: string;
    taxId?: string;
    website?: string;
    logoUrl?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    notes?: string;
    contacts: TenantContactRecord[];
}

const TENANTS_KEY = ["admin", "tenants"] as const;
const TENANT_KEY = (id: string) => ["admin", "tenants", id] as const;

export function useTenants() {
    return useQuery({
        queryKey: TENANTS_KEY,
        queryFn: () => api.get<{ tenants: TenantRecord[] }>("/api/v2/tenants").then((d) => d.tenants),
        staleTime: 10_000,
    });
}

export function useTenant(id: string | undefined) {
    return useQuery({
        queryKey: id ? TENANT_KEY(id) : ["admin", "tenants", "none"],
        queryFn: () =>
            api.get<{ tenant: TenantDetailRecord }>(`/api/v2/tenants/${id}`).then((d) => d.tenant),
        enabled: !!id,
        staleTime: 10_000,
    });
}

interface CreateTenantInput {
    slug: string;
    name: string;
}

export function useCreateTenant() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateTenantInput) =>
            api.post<{ tenant: TenantRecord }>("/api/v2/tenants", input),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: TENANTS_KEY });
        },
    });
}

export interface UpdateTenantInput {
    id: string;
    name?: string;
    status?: TenantStatus;
    legalName?: string;
    industry?: string;
    country?: string;
    taxId?: string;
    website?: string;
    logoUrl?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    notes?: string;
}

export function useUpdateTenant() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, ...patch }: UpdateTenantInput) =>
            api.patch<{ tenant: TenantDetailRecord }>(`/api/v2/tenants/${id}`, patch),
        onSuccess: (_data, vars) => {
            void qc.invalidateQueries({ queryKey: TENANTS_KEY });
            void qc.invalidateQueries({ queryKey: TENANT_KEY(vars.id) });
        },
    });
}

export function useDeleteTenant() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => api.delete<void>(`/api/v2/tenants/${id}`),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: TENANTS_KEY });
        },
    });
}

// ---- contacts ----------------------------------------------------------

export interface CreateContactInput {
    tenantId: string;
    kind: TenantContactKind;
    name: string;
    email: string;
    phone?: string;
    title?: string;
    notes?: string;
}

export function useCreateContact() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ tenantId, ...body }: CreateContactInput) =>
            api.post<{ contact: TenantContactRecord }>(`/api/v2/tenants/${tenantId}/contacts`, body),
        onSuccess: (_data, vars) => {
            void qc.invalidateQueries({ queryKey: TENANT_KEY(vars.tenantId) });
        },
    });
}

export interface UpdateContactInput {
    tenantId: string;
    id: string;
    kind?: TenantContactKind;
    name?: string;
    email?: string;
    phone?: string;
    title?: string;
    notes?: string;
}

export function useUpdateContact() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ tenantId, id, ...patch }: UpdateContactInput) =>
            api.patch<{ contact: TenantContactRecord }>(
                `/api/v2/tenants/${tenantId}/contacts/${id}`,
                patch,
            ),
        onSuccess: (_data, vars) => {
            void qc.invalidateQueries({ queryKey: TENANT_KEY(vars.tenantId) });
        },
    });
}

export function useDeleteContact(tenantId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) =>
            api.delete<void>(`/api/v2/tenants/${tenantId}/contacts/${id}`),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: TENANT_KEY(tenantId) });
        },
    });
}
